import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const tierDetails = {
  free: {
    name: 'Collector',
    price: 'Free',
    color: 'text-muted-foreground',
    bgGradient: 'from-gray-500 to-gray-600',
    borderColor: 'border-border',
    accentColor: 'text-muted-foreground',
    icon: 'User',
    features: [
      'Track up to 50 collectibles',
      'Basic market insights',
      'Community access',
      'Mobile app access',
    ],
  },
  premium: {
    name: 'Curator',
    price: '$19/mo',
    color: 'text-primary',
    bgGradient: 'from-primary to-warning',
    borderColor: 'border-primary/40',
    accentColor: 'text-primary',
    icon: 'Crown',
    features: [
      'Unlimited item tracking',
      'Advanced market analytics',
      'Portfolio performance reports',
      'Price alerts & notifications',
      'Export capabilities',
      'Priority support',
      'API access',
    ],
  },
  professional: {
    name: 'Connoisseur',
    price: '$49/mo',
    color: 'text-purple-400',
    bgGradient: 'from-purple-500 to-purple-700',
    borderColor: 'border-purple-500/40',
    accentColor: 'text-purple-400',
    icon: 'Building',
    features: [
      'Everything in Curator',
      'Multi-user accounts',
      'Custom reporting',
      'White-label options',
      'Dedicated account manager',
      'Custom integrations',
      'Advanced security features',
    ],
  },
  elite: {
    name: 'Elite Collector',
    price: '$24.99/mo',
    color: 'text-warning',
    bgGradient: 'from-warning to-amber-600',
    borderColor: 'border-warning/40',
    accentColor: 'text-warning',
    icon: 'Star',
    features: [
      'Everything in Premium',
      'AI-powered valuations',
      'Auction integration',
      'Professional reports',
      'White-glove support',
      'Early access to features',
      'Custom collection sharing',
    ],
  },
};

const nextSteps = [
  {
    icon: 'LayoutDashboard',
    title: 'Explore Your Dashboard',
    description: 'View your collection overview, analytics, and market insights.',
    action: '/collection-dashboard',
    actionLabel: 'Go to Dashboard',
  },
  {
    icon: 'Plus',
    title: 'Add Your First Collection',
    description: 'Start cataloguing your items and tracking their value.',
    action: '/add-collection',
    actionLabel: 'Add Collection',
  },
  {
    icon: 'TrendingUp',
    title: 'Check Market Analysis',
    description: 'Discover price trends and market movements for your collectibles.',
    action: '/market-analysis',
    actionLabel: 'View Market',
  },
];

const CheckoutConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile } = useAuth();
  const [animateIn, setAnimateIn] = useState(false);
  const [confettiDots] = useState(() =>
    Array.from({ length: 18 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      delay: `${Math.random() * 0.8}s`,
      size: Math.random() > 0.5 ? 'w-2 h-2' : 'w-3 h-3',
      color: ['bg-primary', 'bg-warning', 'bg-success', 'bg-purple-400', 'bg-pink-400']?.[Math.floor(Math.random() * 5)],
    }))
  );

  // Derive tier from query params or location state
  const params = new URLSearchParams(location?.search);
  const tierKey = params?.get('tier') || location?.state?.tier || 'premium';
  const tier = tierDetails?.[tierKey] || tierDetails?.premium;

  const confirmationId = params?.get('ref') || location?.state?.ref || `CRC-${Date.now()?.toString(36)?.toUpperCase()}`;
  const activationDate = new Date()?.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  useEffect(() => {
    const t = setTimeout(() => setAnimateIn(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Confetti layer */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {confettiDots?.map((dot) => (
          <div
            key={dot?.id}
            className={`absolute top-0 rounded-full opacity-0 ${dot?.size} ${dot?.color}`}
            style={{
              left: dot?.left,
              animation: `confettiFall 2.4s ${dot?.delay} ease-out forwards`,
            }}
          />
        ))}
      </div>
      <style>{`
        @keyframes confettiFall {
          0%   { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          80%  { opacity: 0.8; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        @keyframes successPop {
          0%   { transform: scale(0.5); opacity: 0; }
          70%  { transform: scale(1.12); }
          100% { transform: scale(1); opacity: 1; }
        }
        .success-pop { animation: successPop 0.55s cubic-bezier(0.34,1.56,0.64,1) forwards; }
        @keyframes slideUp {
          from { transform: translateY(24px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .slide-up-1 { animation: slideUp 0.5s 0.2s ease-out both; }
        .slide-up-2 { animation: slideUp 0.5s 0.35s ease-out both; }
        .slide-up-3 { animation: slideUp 0.5s 0.5s ease-out both; }
        .slide-up-4 { animation: slideUp 0.5s 0.65s ease-out both; }
      `}</style>
      <main className="relative z-10 flex-1 flex items-start justify-center px-4 py-12">
        <div className="w-full max-w-2xl space-y-6">

          {/* Success Hero Card */}
          <div
            className={`bg-card border ${tier?.borderColor} rounded-2xl p-8 text-center shadow-premium-lg transition-all duration-500 ${
              animateIn ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {/* Animated checkmark */}
            <div className="flex justify-center mb-6">
              <div className={`success-pop w-20 h-20 rounded-full bg-gradient-to-br ${tier?.bgGradient} flex items-center justify-center shadow-gold`}>
                <Icon name="Check" size={40} color="white" />
              </div>
            </div>

            <div className="slide-up-1">
              <p className="text-sm font-medium text-muted-foreground uppercase tracking-widest mb-2">
                Membership Activated
              </p>
              <h1 className="text-3xl font-bold text-foreground mb-1">
                Welcome to{' '}
                <span className={tier?.accentColor}>{tier?.name}</span>
              </h1>
              <p className="text-muted-foreground mt-2 text-base">
                Your account has been upgraded. All features are now active and ready to use.
              </p>
            </div>

            {/* Tier badge */}
            <div className="slide-up-2 mt-6 inline-flex items-center space-x-2 bg-muted/30 border border-border rounded-full px-5 py-2">
              <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${tier?.bgGradient} flex items-center justify-center`}>
                <Icon name={tier?.icon} size={14} color="white" />
              </div>
              <span className={`font-semibold text-sm ${tier?.accentColor}`}>{tier?.name}</span>
              <span className="text-muted-foreground text-sm">·</span>
              <span className="text-muted-foreground text-sm">{tier?.price}</span>
            </div>
          </div>

          {/* Account Activation Details */}
          <div className="slide-up-2 bg-card border border-border rounded-2xl p-6 shadow-sm">
            <h2 className="text-base font-semibold text-foreground mb-4 flex items-center space-x-2">
              <Icon name="FileText" size={18} className="text-muted-foreground" />
              <span>Account Activation Details</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-muted/20 rounded-xl p-4">
                <p className="text-xs text-muted-foreground mb-1">Confirmation ID</p>
                <p className="font-mono text-sm font-semibold text-foreground">{confirmationId}</p>
              </div>
              <div className="bg-muted/20 rounded-xl p-4">
                <p className="text-xs text-muted-foreground mb-1">Activation Date</p>
                <p className="text-sm font-semibold text-foreground">{activationDate}</p>
              </div>
              <div className="bg-muted/20 rounded-xl p-4">
                <p className="text-xs text-muted-foreground mb-1">Account Email</p>
                <p className="text-sm font-semibold text-foreground truncate">
                  {user?.email || userProfile?.email || '—'}
                </p>
              </div>
              <div className="bg-muted/20 rounded-xl p-4">
                <p className="text-xs text-muted-foreground mb-1">Plan Status</p>
                <div className="flex items-center space-x-2">
                  <span className="w-2 h-2 rounded-full bg-success inline-block"></span>
                  <p className="text-sm font-semibold text-success">Active</p>
                </div>
              </div>
            </div>
          </div>

          {/* Unlocked Features */}
          <div className="slide-up-3 bg-card border border-border rounded-2xl p-6 shadow-sm">
            <h2 className="text-base font-semibold text-foreground mb-4 flex items-center space-x-2">
              <Icon name="Unlock" size={18} className={tier?.accentColor} />
              <span>Features Now Unlocked</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {tier?.features?.map((feature, i) => (
                <div key={i} className="flex items-center space-x-3 py-1.5">
                  <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${tier?.bgGradient} flex items-center justify-center flex-shrink-0`}>
                    <Icon name="Check" size={11} color="white" />
                  </div>
                  <span className="text-sm text-foreground">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Next Steps */}
          <div className="slide-up-3 bg-card border border-border rounded-2xl p-6 shadow-sm">
            <h2 className="text-base font-semibold text-foreground mb-4 flex items-center space-x-2">
              <Icon name="ArrowRight" size={18} className="text-muted-foreground" />
              <span>Recommended Next Steps</span>
            </h2>
            <div className="space-y-3">
              {nextSteps?.map((step, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between bg-muted/20 hover:bg-muted/40 border border-border/50 rounded-xl p-4 transition-colors duration-200 group"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 rounded-lg bg-surface border border-border flex items-center justify-center flex-shrink-0">
                      <Icon name={step?.icon} size={18} className="text-muted-foreground group-hover:text-foreground transition-colors" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{step?.title}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{step?.description}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(step?.action)}
                    className="text-xs font-medium text-primary hover:text-primary/80 transition-colors whitespace-nowrap ml-4 flex items-center space-x-1"
                  >
                    <span>{step?.actionLabel}</span>
                    <Icon name="ChevronRight" size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="slide-up-4 flex flex-col sm:flex-row gap-3">
            <Button
              variant="default"
              fullWidth
              iconName="LayoutDashboard"
              iconPosition="left"
              onClick={() => navigate('/collection-dashboard')}
              className="liquid-gold"
            >
              Go to Dashboard
            </Button>
            <Button
              variant="outline"
              fullWidth
              iconName="User"
              iconPosition="left"
              onClick={() => navigate('/profile-management')}
            >
              View My Profile
            </Button>
          </div>

          {/* Footer note */}
          <div className="slide-up-4 text-center">
            <p className="text-xs text-muted-foreground">
              A confirmation email has been sent to your registered address. Questions?{' '}
              <button
                onClick={() => navigate('/help-support')}
                className="text-primary hover:underline"
              >
                Contact Support
              </button>
            </p>
          </div>

        </div>
      </main>
    </div>
  );
};

export default CheckoutConfirmation;
